import React, {useEffect, useState} from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Box, Text, HStack, Flex, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Alert, AlertIcon
} from "@chakra-ui/react";
import {api} from "../../../api/timesheetAPI";
import {useNavigate} from "react-router-dom";
import {showSwal} from "../../../common_components/Alert";
import {useAppDispatch, useAppSelector} from "../../../stateManagement/hooks";
import {jiraBoardrefreshNofilters, setTimeTableLoading, timetableRefresh} from "../../../stateManagement/refreshers";
import {Week} from "../types";
import {addDaysToAPIDate, getIsoWeekNumber} from "../../../dateUtils";
import {DEFAULT_CATEGORY, FavoriteEntry, loadFavorites} from "./favorites";
import {LocationToDefaultCategory} from "../../WorklogModals/hourCategories";
import {absenceBlockMessage, isAbsenceCodeBlocked} from "./absenceRestriction";
import {buildCopyActions, buildCopyRows, summarizeWeekForOverview, WeekOverviewSummary, WEEKDAY_NAMES} from "./rowMapping";
import {buildWorkingRows, EditableWeekTable, rowsToResult, WorkingRow} from "./EditableWeekTable";
import {ProgressOverlay} from "./ProgressOverlay";
import {saveWorklogEntry} from "./weekActionsApi";
import {formatHourString} from "./hourPrecision";
import {InfoBar} from "./WeekActionsUI";
import {useIsMobile} from "../../../utils/useIsMobile";

interface CopyWeekDialogProps {
    isOpen: boolean;
    onClose: () => void;
    targetAnchor: string; // Monday of the currently displayed week (ISO date)
}

type Phase = "pickWeek" | "review" | "progress";

export function CopyWeekDialog({isOpen, onClose, targetAnchor}: CopyWeekDialogProps) {
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const impersonate = useAppSelector((s) => s.impersonation.impersonatedUser);
    const currentUserName = useAppSelector((s) => s.user.user.name);
    const userLocation = useAppSelector((s) => s.user.user.location);
    const preferencesDefaultCategory = useAppSelector((s) => s.user.preferences.defaultCategory);
    const category = preferencesDefaultCategory || LocationToDefaultCategory[userLocation] || DEFAULT_CATEGORY;

    const [phase, setPhase] = useState<Phase>("pickWeek");
    const [offsetWeeks, setOffsetWeeks] = useState(1);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState<WeekOverviewSummary | null>(null);
    const [rows, setRows] = useState<WorkingRow[]>([]);
    const [targetDates, setTargetDates] = useState<string[]>([]);
    const [targetWeek, setTargetWeek] = useState<Week | null>(null);
    const [jiraBaseUrl, setJiraBaseUrl] = useState("");
    const [favorites, setFavorites] = useState<FavoriteEntry[]>([]);
    const [progress, setProgress] = useState({done: 0, total: 0, message: ""});
    const [validationError, setValidationError] = useState("");

    const sourceAnchor = addDaysToAPIDate(targetAnchor, -7 * offsetWeeks);

    useEffect(() => {
        if (!isOpen) { return; }
        setPhase("pickWeek");
        setOffsetWeeks(1);
        setValidationError("");
        setFavorites(loadFavorites(currentUserName));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || phase !== "pickWeek") { return; }
        let cancelled = false;
        setPreviewLoading(true);
        api.getTimeTable(sourceAnchor, "weekly", impersonate).then((res) => {
            if (cancelled) { return; }
            setPreview(summarizeWeekForOverview(res.data.weeks[0]));
        }).catch(() => {
            if (!cancelled) { setPreview(null); }
        }).finally(() => { if (!cancelled) { setPreviewLoading(false); } });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, phase, sourceAnchor, impersonate]);

    async function handleContinue() {
        try {
            const [sourceRes, targetRes] = await Promise.all([
                api.getTimeTable(sourceAnchor, "weekly", impersonate),
                api.getTimeTable(targetAnchor, "weekly", impersonate)
            ]);
            const sourceWeek: Week = sourceRes.data.weeks[0];
            const targetWeekData: Week = targetRes.data.weeks[0];
            const {rows: builtRows, blockedJiraIds} = buildCopyRows(sourceWeek, targetWeekData, (jiraId) => isAbsenceCodeBlocked(jiraId, currentUserName));

            if (blockedJiraIds.length > 0) {
                showSwal("failed", `${absenceBlockMessage(currentUserName)}\n\nSkipped code(s): ${blockedJiraIds.join(", ")}`);
            }
            if (builtRows.length === 0) {
                showSwal("failed", "Source week contains no booked entries.");
                return;
            }

            setTargetWeek(targetWeekData);
            setTargetDates(targetWeekData.week_dates);
            setJiraBaseUrl(sourceRes.data.JIRA_BASE_URL || targetRes.data.JIRA_BASE_URL || "");
            setRows(buildWorkingRows(builtRows));
            setPhase("review");
        } catch (err: any) {
            showSwal("failed", err?.response?.data ?? String(err));
        }
    }

    async function handleCopy() {
        if (!targetWeek) { return; }
        const result = rowsToResult(rows, "copy");
        if (result === null) {
            setValidationError("Hours must be empty or numbers between 0 and 24.");
            return;
        }
        setValidationError("");
        const {actions, conflicts} = buildCopyActions(result, targetWeek, targetDates);

        if (conflicts.length > 0) {
            const lines = conflicts.slice(0, 12).map((c) => `\u2022 ${c.jiraId} on ${c.day} (${c.date})${c.comment ? ` \u2014 "${c.comment}"` : ""}`);
            showSwal("failed", `${conflicts.length} entr${conflicts.length === 1 ? "y" : "ies"} conflict with an existing not-approved booking:\n\n${lines.join("\n")}`);
            return;
        }
        if (actions.length === 0) {
            showSwal("failed", "No positive hour values are left in the table.");
            return;
        }

        setPhase("progress");
        dispatch(setTimeTableLoading(true));
        setProgress({done: 0, total: actions.length, message: "Starting..."});
        const failures: {jiraId: string; date: string; reason: string}[] = [];
        try {
            for (let i = 0; i < actions.length; i += 1) {
                const action = actions[i];
                setProgress({done: i, total: actions.length, message: `${action.jiraId} ${action.date}`});
                try {
                    await saveWorklogEntry({jiraId: action.jiraId, category, date: action.date, hours: action.hours, comment: action.comment, impersonate});
                } catch (err: any) {
                    console.error("WeekActions: copy failed for", action.jiraId, action.date, err);
                    failures.push({jiraId: action.jiraId, date: action.date, reason: err?.response?.data ? String(err.response.data) : String(err)});
                }
                setProgress({done: i + 1, total: actions.length, message: `${action.jiraId} ${action.date}`});
            }
        } finally {
            dispatch(timetableRefresh());
            dispatch(jiraBoardrefreshNofilters());
            dispatch(setTimeTableLoading(false));
            setPhase("pickWeek");
            onClose();
        }
        if (failures.length > 0) {
            const lines = failures.slice(0, 12).map((f) => `\u2022 ${f.jiraId} on ${f.date} \u2014 ${f.reason}`);
            showSwal("failed", `${failures.length} of ${actions.length} entries failed to copy and were skipped (the rest were copied):\n\n${lines.join("\n")}`);
        }
    }

    if (phase === "progress") {
        return <ProgressOverlay isOpen title="Copying entries..." done={progress.done} total={progress.total} message={progress.message}/>;
    }

    if (phase === "pickWeek") {
        const sourceSunday = addDaysToAPIDate(sourceAnchor, 6);
        return (
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl">
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>Select Source Week</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <InfoBar>
                            <Text><b>Target week:</b> {targetAnchor} to {addDaysToAPIDate(targetAnchor, 6)}</Text>
                            <Text><b>Source week:</b> {sourceAnchor} to {sourceSunday}</Text>
                        </InfoBar>
                        <HStack justify="space-between" mb={3} spacing={2} align="center">
                            <Button size={isMobile ? "md" : "sm"} onClick={() => setOffsetWeeks((v) => v + 1)} aria-label="Older week">&lt;{isMobile ? "" : " Older"}</Button>
                            <Text fontWeight={600} textAlign="center" fontSize={isMobile ? "sm" : undefined}>
                                Week {getIsoWeekNumber(sourceAnchor)}{isMobile ? <br/> : " "}({sourceAnchor} to {sourceSunday})
                            </Text>
                            <Button size={isMobile ? "md" : "sm"} isDisabled={offsetWeeks === 1} onClick={() => setOffsetWeeks((v) => Math.max(1, v - 1))} aria-label="Newer week">{isMobile ? "" : "Newer "}&gt;</Button>
                        </HStack>
                        {previewLoading ? (
                            <Spinner/>
                        ) : preview ? (
                            isMobile ? <WeekPreviewCards preview={preview}/> : <WeekPreviewTable preview={preview}/>
                        ) : (
                            <Text color="red.600" _dark={{color: "red.300"}}>Unable to load source-week preview.</Text>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleContinue}>Continue</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl">
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>Review And Edit Week Copy</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <InfoBar>
                            <Text>From: {sourceAnchor} to {addDaysToAPIDate(sourceAnchor, 4)}</Text>
                            <Text>To: {targetAnchor} to {addDaysToAPIDate(targetAnchor, 4)}</Text>
                            <Text>Category: {category} | Overtime: false</Text>
                        </InfoBar>
                        {validationError && (
                            <Alert status="error" mb={3}><AlertIcon/>{validationError}</Alert>
                        )}
                        <EditableWeekTable
                            rows={rows}
                            setRows={setRows}
                            mode="copy"
                            jiraBaseUrl={jiraBaseUrl}
                            favorites={favorites}
                            onManageFavorites={() => navigate("/preferences")}
                        />
                        <Text mt={2} fontSize="xs" color="text-muted">Gray cells already have a booking in the current week and are excluded from copying.</Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleCopy}>Copy</Button>
                    </ModalFooter>
                </ModalContent>
        </Modal>
    );
}

function WeekPreviewCards({preview}: {preview: WeekOverviewSummary}) {
    return (
        <Box>
            <Text fontSize="sm" mb={2} color="text-muted">
                {preview.entryCount} entries · {formatHourString(preview.totalHours)}h total · {formatHourString(preview.weekdayHours)}h Mon-Fri
            </Text>
            {preview.rows.length === 0 && (
                <Box borderWidth="1px" borderStyle="dashed" borderColor="border-subtle" borderRadius="lg" py={6} textAlign="center">
                    <Text color="text-subtle">No booked entries in this week.</Text>
                </Box>
            )}
            {preview.rows.map((row) => (
                <Box key={row.jiraId} borderWidth="1px" borderColor="border-subtle" borderRadius="lg" p={3} mb={2} bg="surface">
                    <Flex justify="space-between" align="baseline" gap={2}>
                        <Text fontWeight={700} color="blue.600" _dark={{color: "var(--tb-key-link)"}}>{row.jiraId}</Text>
                        <Text fontWeight={700}>{formatHourString(row.rowSum)}h</Text>
                    </Flex>
                    <Text fontSize="sm" noOfLines={2} mb={2}>{row.summary}</Text>
                    <Flex gap={1}>
                        {row.hours.map((h, i) => (
                            <Box key={i} flex="1" textAlign="center" borderRadius="md" py="3px"
                                 bg={h > 0 ? "surface-subtle" : "transparent"}>
                                <Text fontSize="10px" color="text-muted">{WEEKDAY_NAMES[i]}</Text>
                                <Text fontSize="xs" fontWeight={h > 0 ? 700 : 400} color={h > 0 ? undefined : "text-subtle"}>{h > 0 ? formatHourString(h) : "–"}</Text>
                            </Box>
                        ))}
                    </Flex>
                </Box>
            ))}
        </Box>
    );
}

function WeekPreviewTable({preview}: {preview: WeekOverviewSummary}) {
    return (
        <Box>
            <Text fontSize="sm" mb={2}>
                Entries: {preview.entryCount} | Total: {formatHourString(preview.totalHours)}h | Mon-Fri: {formatHourString(preview.weekdayHours)}h
            </Text>
            <TableContainer maxH="360px" overflowY="auto" border="1px solid" borderColor="border-subtle" borderRadius="md">
                <Table size="sm">
                    <Thead position="sticky" top={0} bg="surface-subtle">
                        <Tr>
                            <Th>Key</Th>
                            <Th>Summary</Th>
                            {WEEKDAY_NAMES.map((d) => <Th key={d} textAlign="center">{d}</Th>)}
                            <Th textAlign="center">Total</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {preview.rows.length === 0 ? (
                            <Tr><Td colSpan={10} textAlign="center" color="text-subtle">No booked entries in this week.</Td></Tr>
                        ) : preview.rows.map((row) => (
                            <Tr key={row.jiraId}>
                                <Td color="blue.600" _dark={{color: "var(--tb-key-link)"}} fontWeight={600}>{row.jiraId}</Td>
                                <Td>{row.summary}</Td>
                                {row.hours.map((h, i) => <Td key={i} textAlign="center">{h > 0 ? `${formatHourString(h)}h` : ""}</Td>)}
                                <Td textAlign="center" fontWeight={600}>{formatHourString(row.rowSum)}h</Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>
        </Box>
    );
}
